import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { addDays, addHours, addYears, format } from 'date-fns'
import { CalendarIcon, Loader } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { Suspense, useEffect, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { CopyField } from '@/components/sv/copy-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useCompanyBilling, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { CreateApiKeySheetFallback } from '@/routes/api-keys-v2/create-api-key-sheet/fallback'
import {
  PermissionSelector,
  type CompanyResources,
} from '@/routes/api-keys-v2/create-api-key-sheet/permission-selector'
import {
  EXPIRATION_PRESETS,
  createApiKeySchema,
  type CreateApiKeyFormValues,
  type ExpirationPreset,
} from '@/routes/api-keys-v2/create-api-key-sheet/schema'
import type {
  CreateApiKeyMutation,
  CreateApiKeyMutationVariables,
  GetApiKeyRolesAndPermissionsQuery,
  GetApiKeyRolesAndPermissionsQueryVariables,
} from '@/routes/api-keys-v2/create-api-key-sheet/index.types.gen'

export const GET_API_KEY_ROLES_AND_PERMISSIONS = gql`
  query GetApiKeyRolesAndPermissions($idCompany: String!) {
    company(id: $idCompany) {
      id
      apiKeyRoles {
        id
        name
        description
        permissions
      }
      apiKeyPermissions {
        id
        name
        description
        resource
        action
      }
      apps @live {
        id
        items {
          id
          displayName
        }
      }
      databases @live {
        id
        items {
          id
          displayName
        }
      }
      staticSites @live {
        id
        items {
          id
          displayName
        }
      }
      objectStorages @live {
        id
        items {
          id
          displayName
        }
      }
      edgeLoadBalancers @live {
        id
        items {
          id
          displayName
        }
      }
      webhooks @live {
        id
        items {
          id
          endpoint
        }
      }
      apiKeys {
        id
        name
      }
      dockerRegistryCredentials {
        id
        name
      }
    }
  }
`

const CREATE_API_KEY = gql`
  mutation CreateApiKey(
    $idCompany: String!
    $name: String!
    $idRoles: [String!]
    $capabilities: [ApiKeyCapabilityInput!]
    $expiredAt: Date
  ) {
    createApiKey(
      idCompany: $idCompany
      name: $name
      idRoles: $idRoles
      capabilities: $capabilities
      expiredAt: $expiredAt
    ) {
      token
      name
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const PRESET_LABELS: Record<ExpirationPreset, string> = {
  never: 'Never',
  '12h': '12h',
  '1d': '1d',
  '2d': '2d',
  '7d': '7d',
  '30d': '30d',
  custom: 'Custom',
}

const computeExpiredAt = (preset: ExpirationPreset): Date | null => {
  const now = new Date()
  switch (preset) {
    case 'never':
      return null
    case '12h':
      return addHours(now, 12)
    case '1d':
      return addDays(now, 1)
    case '2d':
      return addDays(now, 2)
    case '7d':
      return addDays(now, 7)
    case '30d':
      return addDays(now, 30)
    case 'custom':
      return null
  }
}

const now = new Date()
const startMonth = now
const endMonth = addYears(now, 5)

const Content = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()
  const [token, setToken] = useState<string>('')
  const [calendarOpen, setCalendarOpen] = useState(false)
  const { hasValidBankcard } = useCompanyBilling()

  const { data: rolesData } = useSuspenseQuery<
    GetApiKeyRolesAndPermissionsQuery,
    GetApiKeyRolesAndPermissionsQueryVariables
  >(GET_API_KEY_ROLES_AND_PERMISSIONS, { variables: { idCompany } })

  const roles = rolesData?.company?.apiKeyRoles ?? []
  const permissions = rolesData?.company?.apiKeyPermissions ?? []
  const companyResources: CompanyResources = {
    app: rolesData?.company?.apps?.items ?? [],
    database: rolesData?.company?.databases?.items ?? [],
    static_site: rolesData?.company?.staticSites?.items ?? [],
    object_storage: rolesData?.company?.objectStorages?.items ?? [],
    load_balancer: rolesData?.company?.edgeLoadBalancers?.items ?? [],
    webhook: (rolesData?.company?.webhooks?.items ?? []).map((w) => ({
      id: w.id,
      displayName: w.endpoint,
    })),
    api_key: (rolesData?.company?.apiKeys ?? []).map((k) => ({
      id: k.id,
      displayName: k.name,
    })),
    registry_credential: (
      rolesData?.company?.dockerRegistryCredentials ?? []
    ).map((r) => ({
      id: r.id,
      displayName: r.name,
    })),
  }

  const [createApiKey] = useMutation<
    CreateApiKeyMutation,
    CreateApiKeyMutationVariables
  >(CREATE_API_KEY, { refetchQueries: ['GetCompanyApiKeys'] })

  const form = useForm<CreateApiKeyFormValues>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: {
      name: '',
      expirationPreset: 'never' as ExpirationPreset,
      expiredAt: null,
      permissionMode: 'role',
      idRole: '',
      capabilities: [],
    },
  })

  const { handleSubmit, reset } = form

  useEffect(() => {
    if (!isOpen) {
      setToken('')
      reset({
        name: '',
        expirationPreset: 'never' as ExpirationPreset,
        expiredAt: null,
        permissionMode: 'role',
        idRole: '',
        capabilities: [],
      })
    }
  }, [isOpen, reset])

  const onSubmit = async (values: CreateApiKeyFormValues) => {
    if (!hasValidBankcard) {
      toast(
        <ToasterContent variant="error">
          Add a payment method to create an API key
        </ToasterContent>,
        { position: 'top-right' }
      )
      return
    }

    if (isPending) {
      return
    }

    startTransition(async () => {
      try {
        const { data } = await createApiKey({
          variables: {
            idCompany,
            name: values.name,
            idRoles:
              values.permissionMode === 'role' && values.idRole
                ? [values.idRole]
                : undefined,
            capabilities:
              values.permissionMode === 'custom'
                ? (values.capabilities ?? []).flatMap((cap) => {
                    const resource = cap.permission.split(':')[0]?.toLowerCase()
                    const scopingMap = form.getValues('resourceScoping')
                    const scoping = scopingMap?.[resource ?? '']
                    if (!scoping || scoping.mode === 'all') {
                      return [{ permission: cap.permission }]
                    }
                    return scoping.ids.map((id) => ({
                      permission: cap.permission,
                      idResource: id,
                    }))
                  })
                : undefined,
            expiredAt:
              values.expirationPreset === 'never'
                ? undefined
                : values.expirationPreset === 'custom'
                  ? values.expiredAt
                  : computeExpiredAt(values.expirationPreset ?? 'never'),
          },
        })
        setToken(data?.createApiKey?.token ?? '')
        toast(
          <ToasterContent variant="success">
            API key created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create API key
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <div className="px-4 overflow-y-auto w-full pb-2 h-full">
        <AnimatePresence mode="wait" initial={false}>
          {token ? (
            <motion.div
              className="space-y-2"
              key="view-api-token"
              initial={{ opacity: 0, y: -8, height: 0, filter: 'blur(6px)' }}
              animate={{
                opacity: 1,
                y: 0,
                height: 'auto',
                filter: 'blur(0px)',
              }}
              exit={{ opacity: 0, y: -8, height: 0, filter: 'blur(6px)' }}
            >
              <Label>API token</Label>
              <div className="text-xs text-muted-foreground">
                Copy the API key and store it. This is the only time it is
                displayed.
              </div>
              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: {
                    transition: {
                      delayChildren: 0.15,
                    },
                  },
                }}
              >
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 6 },
                    show: { opacity: 1, y: 0 },
                  }}
                >
                  <CopyField value={token} className="w-full" />
                </motion.div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="create-api-token"
              initial={{ opacity: 0, y: 8, height: 0, filter: 'blur(6px)' }}
              animate={{
                opacity: 1,
                y: 0,
                height: 'auto',
                filter: 'blur(0px)',
              }}
              exit={{ opacity: 0, y: 8, height: 0, filter: 'blur(6px)' }}
            >
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="off"
                            aria-label="API key name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="expirationPreset"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Expiration</FormLabel>
                        <FormControl>
                          <RadioGroup
                            className="flex flex-wrap gap-2"
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value)
                              if (value !== 'custom') {
                                form.setValue('expiredAt', null)
                                setCalendarOpen(false)
                              }
                            }}
                          >
                            {EXPIRATION_PRESETS.map((preset) => (
                              <Label
                                key={preset}
                                className={cn(
                                  'flex items-center h-9 rounded-md border px-3 cursor-pointer text-sm font-medium',
                                  'has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20'
                                )}
                              >
                                <RadioGroupItem
                                  value={preset}
                                  className="sr-only"
                                />
                                {PRESET_LABELS[preset]}
                              </Label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        {field.value === 'custom' ? (
                          <FormField
                            control={form.control}
                            name="expiredAt"
                            render={({ field }) => (
                              <div>
                                <div className="flex items-end gap-4 mt-2">
                                  <FormItem className="flex flex-col">
                                    <FormLabel>Expires at</FormLabel>
                                    <Popover
                                      open={calendarOpen}
                                      onOpenChange={setCalendarOpen}
                                    >
                                      <PopoverTrigger asChild>
                                        <FormControl>
                                          <Button
                                            variant="outline"
                                            className={cn(
                                              'w-[180px] pl-3 text-left font-normal',
                                              !field.value &&
                                                'text-muted-foreground'
                                            )}
                                          >
                                            {field.value ? (
                                              format(
                                                field.value,
                                                'MMMM dd, yyyy'
                                              )
                                            ) : (
                                              <span>Pick a date</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                          </Button>
                                        </FormControl>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="w-auto p-0"
                                        align="start"
                                      >
                                        <Calendar
                                          mode="single"
                                          selected={field.value as Date}
                                          startMonth={startMonth}
                                          endMonth={endMonth}
                                          onSelect={(value) => {
                                            if (value) {
                                              field.onChange(value)
                                              setCalendarOpen(false)
                                            }
                                          }}
                                          disabled={(date) =>
                                            date <= startMonth
                                          }
                                          captionLayout="dropdown"
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </FormItem>
                                  <FormItem className="flex flex-col">
                                    <FormControl>
                                      <Input
                                        value={format(
                                          field.value ?? 0,
                                          'HH:mm:ss'
                                        )}
                                        onChange={(e) => {
                                          const value = e.target.value
                                          const [hours, minutes, seconds] =
                                            value?.split?.(':') ?? [
                                              '00',
                                              '00',
                                              '00',
                                            ]
                                          const expiredAt = new Date(
                                            field.value ?? 0
                                          )
                                          expiredAt.setHours(
                                            Number(hours ?? '00')
                                          )
                                          expiredAt.setMinutes(
                                            Number(minutes ?? '00')
                                          )
                                          expiredAt.setSeconds(
                                            Number(seconds ?? '00')
                                          )
                                          field.onChange(expiredAt)
                                        }}
                                        type="time"
                                        step="1"
                                        aria-label="Expiration time"
                                        className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                                      />
                                    </FormControl>
                                  </FormItem>
                                </div>
                                <div className="mt-1">
                                  <FormMessage />
                                </div>
                              </div>
                            )}
                          />
                        ) : null}
                      </FormItem>
                    )}
                  />

                  <PermissionSelector
                    form={form}
                    roles={roles}
                    permissions={permissions}
                    companyResources={companyResources}
                  />

                  <Button type="submit" hidden />
                </form>
              </Form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <SheetFooter className="sticky">
        {token ? (
          <Button
            onClick={() =>
              navigator.clipboard.writeText(token ?? '').then(() => onClose())
            }
          >
            Copy API token and close
          </Button>
        ) : (
          <Button
            disabled={isPending || !hasValidBankcard}
            onClick={handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Create API key
          </Button>
        )}
      </SheetFooter>
    </>
  )
}

export const CreateApiKeySheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create API key</SheetTitle>
        </SheetHeader>
        <Suspense fallback={<CreateApiKeySheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
